import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X, Loader2, ExternalLink, FileText, AlertTriangle, ZoomIn, ZoomOut, RotateCcw, History, Pencil, Save, CheckCheck } from "lucide-react";

const FLAG_LABELS = {
  accredited_over_registered: "Accredited > Registered",
  votes_over_accredited: "Votes > Accredited",
  candidate_zero_votes: "Candidate got 0 votes",
  mismatch_high: "Mismatch > 5%",
};
const FLAG_HELP = {
  accredited_over_registered: "Reported accredited voters exceed registered voters — impossible unless the register is wrong.",
  votes_over_accredited: "Total candidate votes exceed accredited voters. Verify the sheet.",
  candidate_zero_votes: "One or more candidates recorded exactly zero votes. Common in strongholds — but worth spot-checking.",
  mismatch_high: "Sum of candidate votes deviates from Valid total by more than 5%.",
};

export default function Approvals() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [elections, setElections] = useState([]);
  const [electionId, setElectionId] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [results, setResults] = useState([]);
  const [locs, setLocs] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [approveAllBusy, setApproveAllBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const [editing, setEditing] = useState(false);
  const [editVotes, setEditVotes] = useState({});
  const [editMeta, setEditMeta] = useState({ total_registered: 0, total_accredited: 0, total_valid: 0, total_rejected: 0, notes: "" });
  const [editReason, setEditReason] = useState("");
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  const pendingCount = useMemo(() => results.filter(r => r.status === "pending").length, [results]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/elections");
      setElections(data);
      if (data.length) setElectionId(data.find(e=>e.status==="active")?.id || data[0].id);
      const { data: allLocs } = await api.get("/locations");
      setLocs(Object.fromEntries(allLocs.map(l => [l.id, l])));
    })();
  }, []);

  const load = async () => {
    if (!electionId) return;
    const wantSuperseded = statusFilter === "all" || statusFilter === "rejected";
    const [r, c] = await Promise.all([
      api.get("/results", { params: { election_id: electionId, status: statusFilter === "all" ? undefined : statusFilter, include_superseded: wantSuperseded || undefined } }),
      api.get(`/elections/${electionId}/candidates`),
    ]);
    setResults(r.data); setCandidates(c.data);
    setSelected(r.data[0] || null);
  };
  useEffect(() => { load(); }, [electionId, statusFilter]);

  useEffect(() => {
    setZoom({ scale: 1, x: 0, y: 0 });
    setEditing(false);
    if (selected?.id) {
      api.get(`/results/${selected.id}/history`).then(r => setHistory(r.data)).catch(() => setHistory([]));
      setEditVotes({ ...(selected.votes || {}) });
      setEditMeta({
        total_registered: selected.total_registered ?? 0,
        total_accredited: selected.total_accredited ?? 0,
        total_valid: selected.total_valid ?? 0,
        total_rejected: selected.total_rejected ?? 0,
        notes: selected.notes || "",
      });
    } else {
      setHistory([]);
    }
    setEditReason("");
  }, [selected?.id]);

  const resetZoom = () => setZoom({ scale: 1, x: 0, y: 0 });
  const zoomBy = (delta) => setZoom(z => ({ ...z, scale: Math.max(1, Math.min(6, z.scale + delta)) }));
  const sheetBoxRef = useRef(null);
  useEffect(() => {
    const el = sheetBoxRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!selected?.sheet_path) return;
      e.preventDefault();
      setZoom(z => ({ ...z, scale: Math.max(1, Math.min(6, z.scale + (e.deltaY > 0 ? -0.2 : 0.2))) }));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [selected?.sheet_path]);
  const onMouseDown = (e) => {
    if (zoom.scale <= 1) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, baseX: zoom.x, baseY: zoom.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setZoom(z => ({ ...z, x: dragRef.current.baseX + (e.clientX - dragRef.current.startX), y: dragRef.current.baseY + (e.clientY - dragRef.current.startY) }));
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  const puPath = (id) => {
    const pu = locs[id]; if (!pu) return "";
    const ward = locs[pu.parent_id] || {};
    const lga = locs[ward.parent_id] || {};
    const st = locs[lga.parent_id] || {};
    return [st.name, lga.name, ward.name, pu.name].filter(Boolean).join(" › ");
  };

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/results/${selected.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Provide a rejection reason");
    setBusy(true);
    try { await api.post(`/results/${selected.id}/reject`, { reason: rejectReason }); toast.success("Rejected"); setRejectReason(""); load(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editReason.trim()) return toast.error("Please add a short reason for the correction");
    setBusy(true);
    try {
      const payload = {
        votes: Object.fromEntries(Object.entries(editVotes).map(([k, v]) => [k, parseInt(v) || 0])),
        total_registered: parseInt(editMeta.total_registered) || 0,
        total_accredited: parseInt(editMeta.total_accredited) || 0,
        total_valid: parseInt(editMeta.total_valid) || 0,
        total_rejected: parseInt(editMeta.total_rejected) || 0,
        notes: editMeta.notes,
        reason: editReason.trim(),
      };
      const { data } = await api.patch(`/results/${selected.id}`, payload);
      toast.success("Result updated");
      setEditing(false); setEditReason("");
      // refresh list + keep the edited item selected
      const wantSuperseded = statusFilter === "all" || statusFilter === "rejected";
      const [r, c] = await Promise.all([
        api.get("/results", { params: { election_id: electionId, status: statusFilter === "all" ? undefined : statusFilter, include_superseded: wantSuperseded || undefined } }),
        api.get(`/elections/${electionId}/candidates`),
      ]);
      setResults(r.data); setCandidates(c.data);
      setSelected(r.data.find(x => x.id === data.id) || data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approveAll = async () => {
    if (!confirm(`Approve ALL ${pendingCount} pending results for this election?`)) return;
    setApproveAllBusy(true);
    try {
      const { data } = await api.post("/results/approve-all", null, { params: { election_id: electionId }});
      toast.success(`Approved ${data.approved} result${data.approved === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setApproveAllBusy(false); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("ec_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/elections/${electionId}/export?auth=${token}`, "_blank");
  };
  const exportPdf = () => {
    const token = localStorage.getItem("ec_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/elections/${electionId}/export/pdf?auth=${token}`, "_blank");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px]">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Verification</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="approvals-title">Approvals</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2" data-testid="export-csv-btn">
            <ExternalLink size={14}/> CSV
          </button>
          <button onClick={exportPdf} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2" data-testid="export-pdf-btn">
            <FileText size={14}/> Official PDF
          </button>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <select value={electionId} onChange={(e)=>setElectionId(e.target.value)} className="bg-transparent border border-white/20 px-3 py-2 text-sm outline-none" data-testid="approvals-election-select">
          {elections.map(e => <option key={e.id} value={e.id} className="bg-black">{e.name}</option>)}
        </select>
        <div className="flex border border-white/20 max-w-full overflow-x-auto">
          {["pending","approved","rejected","all"].map(s => (
            <button key={s} onClick={()=>setStatusFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] whitespace-nowrap ${statusFilter===s?"bg-white text-black":"hover:bg-white/10"}`}>{s}</button>
          ))}
        </div>
        {pendingCount >= 2 && statusFilter === "pending" && (
          <button onClick={approveAll} disabled={approveAllBusy} data-testid="approve-all-btn"
            className="ml-auto px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-50 flex items-center gap-2">
            {approveAllBusy ? <Loader2 size={13} className="animate-spin"/> : <CheckCheck size={13}/>}
            Approve all · {pendingCount}
          </button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6 mt-6">
        {/* List */}
        <div className="col-span-12 lg:col-span-3 border border-white/10 bg-[#111114]">
          <div className="px-4 py-3 border-b border-white/10 text-xs uppercase tracking-[0.3em] text-zinc-500">{results.length} Results</div>
          <div className="max-h-[80vh] overflow-y-auto divide-y divide-white/5">
            {results.map(r => (
              <button key={r.id} onClick={()=>setSelected(r)} className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] ${selected?.id===r.id?"bg-white/5":""}`} data-testid={`result-row-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 ${r.status==="approved"?"bg-emerald-500":r.status==="rejected"?"bg-red-500":"bg-amber-400"}`}/>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{r.status}</span>
                  {r.mismatch > 0 && (
                    <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400" data-testid={`row-mismatch-${r.id}`} title={`Vote sum differs from valid total by ${r.mismatch}`}>
                      <AlertTriangle size={11}/> ±{r.mismatch}
                    </span>
                  )}
                </div>
                <div className="text-sm mt-1 truncate">{puPath(r.polling_unit_id)}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{(r.submitted_by_email||"").split("@")[0]} · {(r.submitted_at||"").replace("T"," ").slice(0,16)}</div>
              </button>
            ))}
            {!results.length && <div className="p-6 text-zinc-500 text-sm">No results.</div>}
          </div>
        </div>

        {/* Detail */}
        <div className="col-span-12 lg:col-span-9">
          {selected ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div ref={sheetBoxRef} className="lg:sticky lg:top-6 self-start border border-white/10 bg-[#111114] aspect-[3/4] relative overflow-hidden select-none"
                   onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
                {selected.sheet_path ? (
                  <>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${selected.sheet_path}?auth=${localStorage.getItem("ec_token")}`}
                      alt="Sheet"
                      draggable={false}
                      style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`, transformOrigin: "center center", transition: dragRef.current.dragging ? "none" : "transform 120ms ease-out", cursor: zoom.scale > 1 ? (dragRef.current.dragging ? "grabbing" : "grab") : "zoom-in" }}
                      className="w-full h-full object-contain"
                      data-testid="approval-sheet"
                      onDoubleClick={() => zoom.scale > 1 ? resetZoom() : zoomBy(1)}
                    />
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/70 backdrop-blur px-1 py-1 border border-white/10">
                      <button onClick={() => zoomBy(0.4)} data-testid="zoom-in-btn" className="p-1.5 hover:bg-white/10" title="Zoom in"><ZoomIn size={14}/></button>
                      <button onClick={() => zoomBy(-0.4)} data-testid="zoom-out-btn" className="p-1.5 hover:bg-white/10" title="Zoom out"><ZoomOut size={14}/></button>
                      <button onClick={resetZoom} data-testid="zoom-reset-btn" className="p-1.5 hover:bg-white/10" title="Reset"><RotateCcw size={14}/></button>
                      <span className="text-[10px] tabular text-zinc-400 px-2">{Math.round(zoom.scale * 100)}%</span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500 text-sm">No sheet attached</div>
                )}
              </div>

              <div className="space-y-4">
                <div className="border border-white/10 bg-[#111114] p-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Polling Unit</div>
                  <div className="text-lg font-medium mt-1">{puPath(selected.polling_unit_id)}</div>
                  <div className="text-xs text-zinc-500 mt-2">Submitted by <span className="text-white">{selected.submitted_by_email}</span></div>
                  {selected.flags?.length > 0 && (
                    <div className="mt-3 border border-red-500/40 bg-red-500/[0.08] p-3" data-testid="flags-detail">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-300 font-semibold">
                        <AlertTriangle size={12}/> Integrity flags
                      </div>
                      <ul className="mt-2 space-y-1 text-xs text-red-100/80">
                        {selected.flags.map(f => (
                          <li key={f}>• <span className="text-red-200 font-medium">{FLAG_LABELS[f] || f}:</span> {FLAG_HELP[f] || ""}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selected.mismatch > 0 && (
                    <div className="mt-3 border border-amber-400/50 bg-amber-400/10 p-3 flex items-start gap-2" data-testid="mismatch-warning">
                      <AlertTriangle size={16} className="text-amber-400 mt-0.5"/>
                      <div className="text-xs text-amber-200">
                        <div className="uppercase tracking-[0.2em] font-semibold text-amber-300">Reconciliation Mismatch</div>
                        <div className="mt-1">Sum of candidate votes ({selected.vote_sum?.toLocaleString?.() || 0}) differs from recorded valid total ({selected.total_valid?.toLocaleString?.() || 0}) by <span className="tabular font-semibold">{selected.mismatch}</span>. Verify against the sheet before approving.</div>
                      </div>
                    </div>
                  )}
                  {selected.edit_reason && !editing && (
                    <div className="mt-3 border border-emerald-500/30 bg-emerald-500/[0.05] p-3 text-xs" data-testid="edit-reason-banner">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold flex items-center gap-2">
                        <Pencil size={11}/> Corrected by {selected.edited_by_email}
                      </div>
                      <div className="mt-1 text-emerald-100/90">"{selected.edit_reason}"</div>
                    </div>
                  )}
                </div>

                <div className="border border-white/10 bg-[#111114]">
                  <div className="px-4 py-2 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center justify-between">
                    <span>Votes</span>
                    {isAdmin && selected.status === "pending" && !editing && (
                      <button onClick={() => setEditing(true)} data-testid="edit-result-btn"
                        className="text-[10px] uppercase tracking-[0.15em] text-emerald-300 hover:text-emerald-200 flex items-center gap-1">
                        <Pencil size={11}/> Edit
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-white/5">
                    {candidates.map(c => (
                      <div key={c.id} className="px-4 py-2 flex items-center gap-3">
                        <div className="w-1 h-6" style={{ background: c.color }}/>
                        <div className="flex-1 truncate text-sm">{c.name} <span className="text-zinc-500">·{c.party_code}</span></div>
                        {editing ? (
                          <input type="number" min="0" value={editVotes[c.id] ?? 0}
                            onChange={(e)=>setEditVotes(v=>({...v, [c.id]: e.target.value}))}
                            data-testid={`edit-vote-${c.id}`}
                            className="w-28 bg-transparent border border-emerald-500/40 px-2 py-1 text-right tabular text-base outline-none focus:border-emerald-400"/>
                        ) : (
                          <div className="font-display text-lg tabular">{(selected.votes||{})[c.id] || 0}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  {editing ? (
                    <div className="px-4 py-3 border-t border-white/10 grid grid-cols-2 gap-3">
                      {["total_registered","total_accredited","total_valid","total_rejected"].map(k => (
                        <label key={k} className="block">
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace("total_","")}</div>
                          <input type="number" min="0" value={editMeta[k]} onChange={(e)=>setEditMeta(m=>({...m,[k]:e.target.value}))}
                            data-testid={`edit-meta-${k}`}
                            className="w-full bg-transparent border-b border-emerald-500/40 py-1 text-base tabular outline-none focus:border-emerald-400"/>
                        </label>
                      ))}
                      <textarea placeholder="Notes (optional)" value={editMeta.notes} onChange={(e)=>setEditMeta(m=>({...m,notes:e.target.value}))} rows={2}
                        data-testid="edit-notes" className="col-span-2 mt-1 bg-transparent border border-white/15 p-2 text-sm outline-none resize-none focus:border-white"/>
                      <div className="col-span-2 mt-1">
                        <div className="text-[10px] uppercase tracking-wider text-red-300 flex items-center gap-2">
                          <AlertTriangle size={11}/> Reason for correction <span className="text-red-400">(required)</span>
                        </div>
                        <input placeholder="e.g. 'agent typed 250 instead of 205 for NDC — verified against attached sheet'"
                          value={editReason} onChange={e=>setEditReason(e.target.value)}
                          data-testid="edit-reason-input" maxLength={400}
                          className="w-full mt-1 bg-transparent border border-red-500/40 px-3 py-2 text-sm outline-none focus:border-red-400"/>
                        <div className="text-[9px] text-zinc-500 mt-1 tabular">{editReason.length}/400 · saved to audit log</div>
                      </div>
                      <div className="col-span-2 flex gap-2 pt-1">
                        <button onClick={saveEdit} disabled={busy || !editReason.trim()} data-testid="save-edit-btn"
                          className="flex-1 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center justify-center gap-2">
                          {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save changes
                        </button>
                        <button onClick={()=>{ setEditing(false); setEditReason(""); }} disabled={busy} data-testid="cancel-edit-btn"
                          className="flex-1 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-2 border-t border-white/10 grid grid-cols-4 text-xs text-zinc-500">
                      <div>Reg: <span className="text-white tabular">{selected.total_registered}</span></div>
                      <div>Acc: <span className="text-white tabular">{selected.total_accredited}</span></div>
                      <div>Valid: <span className="text-emerald-400 tabular">{selected.total_valid}</span></div>
                      <div>Rej: <span className="text-red-400 tabular">{selected.total_rejected}</span></div>
                    </div>
                  )}
                </div>

                {selected.status === "pending" ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={approve} disabled={busy} data-testid="approve-btn" className="py-3 bg-emerald-500 text-black font-semibold uppercase tracking-[0.2em] text-xs hover:bg-emerald-400 disabled:opacity-50 flex items-center justify-center gap-2">
                        {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>} Approve
                      </button>
                      <button onClick={reject} disabled={busy || !rejectReason} data-testid="reject-btn" className="py-3 bg-red-500 text-black font-semibold uppercase tracking-[0.2em] text-xs hover:bg-red-400 disabled:opacity-40 flex items-center justify-center gap-2">
                        {busy ? <Loader2 size={13} className="animate-spin"/> : <X size={13}/>} Reject
                      </button>
                    </div>
                    <input placeholder="Rejection reason (required to reject)" value={rejectReason} onChange={(e)=>setRejectReason(e.target.value)} className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm outline-none focus:border-white" data-testid="reject-reason-input"/>
                  </div>
                ) : (
                  <div className={`border p-3 text-sm ${selected.status==="approved"?"border-emerald-500/40 text-emerald-400":"border-red-500/40 text-red-400"}`}>
                    <div className="uppercase tracking-[0.2em] text-[10px]">{selected.status}</div>
                    {selected.rejection_reason && <div className="mt-1 text-zinc-300">{selected.rejection_reason}</div>}
                    {selected.approved_by_email && <div className="mt-1 text-xs text-zinc-500">by {selected.approved_by_email}</div>}
                  </div>
                )}

                {history.length > 1 && (
                  <div className="border border-white/10 bg-[#111114]" data-testid="history-panel">
                    <div className="px-4 py-2 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
                      <History size={11}/> Submission History ({history.length} versions)
                    </div>
                    <div className="divide-y divide-white/5">
                      {history.map((h) => {
                        const isCurrent = h.id === selected.id;
                        const isSuperseded = !!h.superseded_by;
                        return (
                          <button key={h.id} onClick={()=>setSelected(h)}
                            className={`w-full text-left px-4 py-2 flex items-center gap-3 text-xs hover:bg-white/[0.03] ${isCurrent?"bg-white/5":""}`}
                            data-testid={`history-item-${h.id}`}>
                            <span className="tabular text-zinc-500">v{h.version || 1}</span>
                            <span className={`w-1.5 h-1.5 ${h.status==="approved"?"bg-emerald-500":h.status==="rejected"?"bg-red-500":"bg-amber-400"}`}/>
                            <span className="uppercase tracking-[0.15em] text-[10px] text-zinc-400">{h.status}</span>
                            <span className="ml-auto text-zinc-500 tabular">{(h.submitted_at||"").replace("T"," ").slice(0,16)}</span>
                            {isSuperseded && <span className="text-[9px] uppercase tracking-wider text-zinc-600">superseded</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500">Select a result to review</div>
          )}
        </div>
      </div>
    </div>
  );
}
